// Tradução dos campos da página de contato para o schema interno que alimenta o
// score do ERP e o histórico no Firebase.
//
// O formulário passou a perguntar ORÇAMENTO do projeto no lugar de faturamento
// da empresa, e passou a perguntar o CARGO. São duas mudanças de fundo no score:
//
// - orçamento é o que a pessoa pretende investir; faturamento era um proxy dele.
//   Agora a pontuação olha direto para o dinheiro do projeto.
// - cargo existia como `decisionMakerRole` e nunca era preenchido (o formulário
//   antigo não perguntava). Agora preenche, e decisor volta a valer pontos.
//
// Em compensação saiu a pergunta de prazo, então urgência não pontua mais. Os
// cortes de tier foram recalibrados para o novo teto (8 pts).

export const FASES = [
  'Só uma ideia',
  'Protótipo',
  'MVP no ar',
  'Produto em operação',
  'Legado para substituir',
];

export const TIPOS_PRODUTO = [
  'App mobile',
  'Plataforma web ou SaaS',
  'Sistema interno',
  'Automação ou integração',
  'Site ou landing page',
  'Ainda não sei',
];

export const PUBLICOS = ['B2B', 'B2C', 'B2B e B2C', 'Time interno', 'Governo'];

export const ORCAMENTOS = [
  'Até R$ 50 mil',
  'R$ 50 mil a R$ 200 mil',
  'R$ 200 mil a R$ 500 mil',
  'R$ 500 mil a R$ 1 milhão',
  'Acima de R$ 1 milhão',
  'Ainda não sei',
];

// O `orcamento` do ERP é um número: o servidor lê o texto e, numa faixa, fica
// com o MENOR valor (ver extrairOrcamento em supabase/functions/_shared/leads.ts).
// Mandar o rótulo cru quebraria em dois pontos: "1 milhão" não tem multiplicador
// conhecido pelo servidor (ele só entende "mil" e "k") e viraria R$ 1,00.
// Por isso aqui vai o número escrito por extenso em dígitos, e o rótulo que a
// pessoa escolheu viaja separado em `orcamento_faixa`.
export const ORCAMENTO_ERP = {
  'Até R$ 50 mil': '50000',
  'R$ 50 mil a R$ 200 mil': '50000 a 200000',
  'R$ 200 mil a R$ 500 mil': '200000 a 500000',
  'R$ 500 mil a R$ 1 milhão': '500000 a 1000000',
  'Acima de R$ 1 milhão': '1000000',
  // Sem número nenhum: o servidor guarda vazio em vez de inventar zero.
  'Ainda não sei': '',
};

// O que a pessoa quer construir, na classificação que o Firebase já usa.
export const CHALLENGE_MAP = {
  'App mobile': 'criar_produto_digital',
  'Plataforma web ou SaaS': 'criar_produto_digital',
  'Sistema interno': 'modernizar_sistema',
  'Automação ou integração': 'automatizar_processos',
  'Site ou landing page': 'criar_produto_digital',
  'Ainda não sei': 'outro',
};

const ORCAMENTO_PONTOS = {
  'Até R$ 50 mil': 1,
  'R$ 50 mil a R$ 200 mil': 2,
  'R$ 200 mil a R$ 500 mil': 3,
  'R$ 500 mil a R$ 1 milhão': 4,
  'Acima de R$ 1 milhão': 4,
  'Ainda não sei': 0,
};

const FASE_PONTOS = {
  'Só uma ideia': 0,
  'Protótipo': 1,
  'MVP no ar': 1,
  'Produto em operação': 2,
  'Legado para substituir': 2,
};

// O cargo é texto livre, então a leitura é por palavra. Quem decide sozinho vale
// 2, quem leva para um comitê vale 1, o resto vale 0. Na dúvida pontua menos:
// superestimar lead distorce a previsão de receita para cima.
const DECISOR = /(s[óo]cio|ceo|founder|fundador|dono|propriet|presidente|diretor|c-?level|cto|cfo|coo|cmo|head)/i;
const INFLUENCIA = /(gerente|gestor|coordenador|manager|l[íi]der|supervisor|product owner|\bpo\b)/i;

export function pontosDoCargo(cargo = '') {
  const t = String(cargo || '');
  if (DECISOR.test(t)) return 2;
  if (INFLUENCIA.test(t)) return 1;
  return 0;
}

/**
 * Score de 0 a 8: orçamento (0-4) + cargo (0-2) + fase do produto (0-2).
 */
export function computeTier({ budget = '', role = '', stage = '' }) {
  const score = (ORCAMENTO_PONTOS[budget] || 0) + pontosDoCargo(role) + (FASE_PONTOS[stage] || 0);

  let tier = 'D';
  if (score >= 7) tier = 'A';
  else if (score >= 5) tier = 'B';
  else if (score >= 3) tier = 'C';

  return { score, tier };
}

export const sanitizePhone = (phone) => {
  const digits = (phone || '').replace(/\D/g, '');
  // Campo é um input tel simples (sem seletor de país), então assume BR.
  if (digits.length >= 10 && digits.length <= 11) return `55${digits}`;
  return digits;
};
